import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function AudioFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const [editingPost, setEditingPost] = useState(null);

  const handleEditPost = (post) => {
    setEditingPost(post);
  };
  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    setEditingPost(null);
  };
  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId));
  };

  const fetchPosts = async (pageNum = 1) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/feed/audio?page=${pageNum}&limit=20`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      if (pageNum === 1) {
        setPosts(data.posts || []);
      } else {
        setPosts((prev) => [...prev, ...(data.posts || [])]);
      }
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching audio feed:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPosts(); }, []);

  const playAudio = (post) => {
    if (currentPlaying?._id === post._id) {
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioRef.current?.play();
        setIsPlaying(true);
      }
    } else {
      setCurrentPlaying(post);
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    if (currentPlaying && audioRef.current) {
      audioRef.current.src = currentPlaying.audioData?.audioUrl || currentPlaying.images?.[0];
      audioRef.current.play();
    }
  }, [currentPlaying]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading && posts.length === 0) {
    return <div className="audio_feed_loading"><div className="audio_loader"></div><span>Carregando áudios...</span></div>;
  }

  return (
    <div className="audio_feed">

      {posts.length === 0 ? (
        <div className="audio_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
          </svg>
          <h3>Nenhum áudio encontrado</h3>
          <p>Seja o primeiro a compartilhar um áudio!</p>
        </div>
      ) : (
        <div className="audio_list">
          {posts.map((post) => (
            <div key={post._id} className={`audio_item ${currentPlaying?._id === post._id ? "playing" : ""}`}>
              <button className="audio_play_btn" onClick={() => playAudio(post)}>
                {currentPlaying?._id === post._id && isPlaying ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M8 5v14l11-7z"/></svg>
                )}
              </button>
              <div className="audio_waveform">
                {(post.audioData?.waveform || Array(20).fill(0.5)).map((h, i) => (
                  <div key={i} className="audio_bar" style={{ height: `${Math.max(20, h * 100)}%` }} />
                ))}
              </div>
              <div className="audio_info">
                <Link to={`/profile/${post.user.username}`} className="audio_author">
                  <img src={post.user.picture} alt="" />
                  <span>{post.user.first_name}</span>
                </Link>
                <div className="audio_meta">
                  <span>{post.audioData?.duration ? formatDuration(post.audioData.duration) : "0:00"}</span>
                  <span>•</span>
                  <Moment fromNow locale="pt-br">{post.createdAt}</Moment>
                </div>
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <FeedPostMenu
                  post={post}
                  onEdit={handleEditPost}
                  onPostDeleted={handlePostDeleted}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="audio_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais"}
          </button>
        </div>
      )}

      <audio ref={audioRef} onEnded={() => setIsPlaying(false)} style={{ display: "none" }} />

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </div>
  );
}
